import { normalizeAllDayOverlayBounds } from "./calendar-overlay-layout";

/**
 * Build a react-big-calendar event for a "busy" overlay/self block on the
 * create/edit scheduler surface from a stored availability record
 * ({ startUtc, endUtc, allDay }).
 *
 * All-day events are normalized to browser-local day bounds via the canonical
 * `normalizeAllDayOverlayBounds` — the SAME helper the scheduler's week/day
 * overlay lanes use. Google stores all-day events as date strings with an
 * exclusive next-day end (`new Date("2026-04-30")` = UTC midnight), so the
 * old create-surface builders — which used `new Date(startUtc)` /
 * `new Date(endUtc)` raw and applied no trim — rendered all-day blocks across
 * the wrong local day(s) for any non-UTC browser. Delegating to the normalizer
 * fixes both the start and the end in every timezone (a stronger fix than the
 * 1ms end-trim in utils/calendar-events.js, which only the voting surface uses).
 *
 * @param {object} opts
 * @param {string} opts.id
 * @param {string} [opts.title="Busy"]
 * @param {string} opts.startUtc  ISO instant, or date string for all-day.
 * @param {string} opts.endUtc    ISO instant, or exclusive date string for all-day.
 * @param {boolean} [opts.allDay=false]
 * @param {object} [opts.resource={}]
 * @returns {{id, title, start: Date, end: Date, allDay: boolean, resource}|null}
 *   null when the instants are missing/invalid (caller should filter it out).
 */
export function buildBusyOverlayEvent({
  id,
  title = "Busy",
  startUtc,
  endUtc,
  allDay = false,
  resource = {},
}) {
  let start = startUtc ? new Date(startUtc) : null;
  let end = endUtc ? new Date(endUtc) : null;
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
    return null;
  }
  if (allDay) {
    // Linked-calendar events carry no tzid; their date-string instants are
    // parsed as UTC midnight, so the normalizer recovers the calendar date
    // from UTC and re-anchors it to browser-local day bounds.
    const norm = normalizeAllDayOverlayBounds({ startUtc, endUtc, tzid: null });
    if (!norm) return null;
    start = new Date(norm.startUtc);
    end = new Date(norm.endUtc);
  }
  return { id, title, start, end, allDay: !!allDay, resource };
}
